import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import Button, { ButtonType } from "../../components/Assets/Button";
import { ITaskData, ITaskRemark } from "../../models/TaskData.model";
import { useNavigate } from "react-router-dom";

interface TaskDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: ITaskData;
  remarks: ITaskRemark[];
  newFollowUpDate: Dayjs | null;
  newFollowUpTime: Dayjs | null;
  setNewFollowUpDate: (date: Dayjs | null) => void;
  setNewFollowUpTime: (time: Dayjs | null) => void;
  completeChecked: boolean;
  setCompleteChecked: (checked: boolean) => void;
  onAddRemark: () => void;
  taskRemarks: string;
  onRemarkChange: (value: string) => void;
}

const TaskDetailsModal: React.FC<TaskDetailsModalProps> = ({
  isOpen,
  onClose,
  task,
  remarks,
  newFollowUpDate,
  newFollowUpTime,
  setNewFollowUpDate,
  setNewFollowUpTime,
  completeChecked,
  setCompleteChecked,
  onAddRemark,
  taskRemarks,
  onRemarkChange,
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
      <div className="relative bg-white rounded-lg shadow-xl w-[95%] max-w-3xl p-4 sm:p-6">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 focus:outline-none"
          aria-label="Close modal"
        >
          <svg
            className="w-6 h-6 fill-current"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              d="M14.348 5.652a.5.5 0 0 0-.707 0L10 9.293 6.36 5.652a.5.5 0 1 0-.707.707L9.293 10l-3.64 3.64a.5.5 0 0 0 .707.707L10 10.707l3.64 3.64a.5.5 0 0 0 .707-.707L10.707 10l3.64-3.64a.5.5 0 0 0 0-.708z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        <div className="w-full bg-white rounded-lg max-h-[70vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
          <div className="flex items-center flex-col sm:flex-row relative">
            <span className="text-xs font-semibold text-gray-800">Task Of</span>
            <span className="text-blue-600 font-medium px-3 py-1 rounded-full text-xs mt-2 sm:mt-0">
              {task?.followUp
                ? dayjs(task.followUp.toString()).format("DD/MM/YYYY HH:mm")
                : "00 Hr"}
            </span>
          </div>

          <div className="text-sm space-y-4 mt-4">
            <p className="text-gray-700">
              <strong className="font-medium text-gray-900">
                Task Summary:
              </strong>{" "}
              {task?.taskSummary}
            </p>

            {task?.lead && (
              <div className="mb-4 p-4 bg-gray-50 rounded-md">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-semibold text-gray-900 ">
                    Lead Details:
                  </h3>

                  <a
                    href={`/lead-details/${task?.lead?._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between cursor-pointer transition"
                  >
                    <span className="text-xs font-medium text-blue-600">
                      View Full Lead Profile
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-blue-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </a>
                </div>
                <div className="flex items-center gap-4">
                  <div>
                    {task.lead.name && (
                      <p className="text-xs text-gray-600">
                        <strong>Name:</strong> {task.lead.name}
                      </p>
                    )}
                    {task.lead.mobileNumber && (
                      <p className="text-xs text-gray-600">
                        <strong>Mobile:</strong> {task.lead.mobileNumber}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">
                      <strong>State:</strong> {task?.lead?.address?.state}
                    </p>
                    <p className="text-xs text-gray-600">
                      <strong>District:</strong> {task?.lead?.address?.district}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="relative">
              <input
                type="text"
                placeholder="Add a remark..."
                value={taskRemarks}
                onChange={(e) => onRemarkChange(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none"
              />

              <div className="mt-4">
                <h3 className="text-sm font-semibold mb-2">
                  Follow Up Date & Time
                </h3>
                <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4">
                  <TimePicker
                    className="w-full"
                    value={newFollowUpTime}
                    onChange={setNewFollowUpTime}
                    ampm
                    format="hh:mm A"
                    views={["hours", "minutes"]}
                  />
                  <DatePicker
                    value={newFollowUpDate}
                    onChange={setNewFollowUpDate}
                    format="DD/MM/YYYY"
                    className="w-full"
                    minDate={dayjs()}
                  />
                </div>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {remarks.map(({ _id, text, date }) => (
                <div
                  key={_id}
                  className="p-2 border rounded-lg shadow-sm text-sm"
                >
                  <p className="text-gray-900 mb-1 font-medium">
                    Remark:{" "}
                    <span className="text-gray-700 font-normal">{text}</span>
                  </p>
                  <p className="text-gray-900 font-medium">
                    Date:{" "}
                    <span className="text-gray-700 font-normal">
                      {new Date(date).toLocaleDateString()}
                    </span>
                  </p>
                </div>
              ))}
            </div>

            <div className="flex items-center mt-2">
              <input
                type="checkbox"
                id="completeTask"
                checked={completeChecked}
                onChange={(e) => setCompleteChecked(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="completeTask" className="text-sm text-gray-700">
                Mark as Complete
              </label>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-end space-y-2 sm:space-y-0 sm:space-x-2">
          <Button
            onClick={onAddRemark}
            type={ButtonType.Submit}
            className="text-xs"
          >
            Add Remark
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TaskDetailsModal;
